import { z } from 'zod'
import { listResourceSchema } from '@/types/schema/api/list-resource-schema'
import { collaborativeItemSchema } from '@/types/schema/collaborative/collaborative-item'
import { postTypeSchema } from '@/types/schema/post-type'
import { PlatformShortcode } from '@/utils/convert-platform'

export const draftPostSchema = z.object({
  url: z.string().url(),
  platform: z.nativeEnum(PlatformShortcode),
  post_id: z.string(),
  post_type: postTypeSchema,
})

export const draftRecordSchema = z.object({
  name: z.string(),
  start_at: z.string(),
  end_at: z.string(),
  item_list: collaborativeItemSchema.array(),
  post_list: draftPostSchema.array(),
})
type DraftRecord = z.infer<typeof draftRecordSchema>

export const postSchema = draftPostSchema.extend({
  id: z.number(),
  title: z.string(),
  content: z.string(),
  like_count: z.number().nullable(),
  is_disable_like: z.boolean().nullish(),
  comment_count: z.number().nullable(),
  share_count: z.number().nullable(),
  view_count: z.number().nullable(),
  play_count: z.number().nullable(),
  thumbnail_url: z.string().url(),
  reward: z.string().transform(Number).nullable(),
  crawled_id: z.string().nullable(),
  ig_post_id: z.string().nullable(),
  reach_count: z.number().nullable(),
  click_count: z.number().nullable(),
  publish_time: z.string(),
})
export type Post = z.infer<typeof postSchema>

const customPostSchema = postSchema
  .omit({
    post_type: true,
    platform: true,
  })
  .extend({
    is_use_handfilled: z.boolean(),
    post_id: z.string().nullable(),
    url: z.string().url().nullable(),
    thumbnail_url: z.string().url().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    publish_time: z.string().nullable(),
  })
  .and(
    z.discriminatedUnion('is_custom_post_type', [
      z.object({
        is_custom_post_type: z.literal(true),
        post_type: z.string(),
      }),
      z.object({
        is_custom_post_type: z.literal(false),
        post_type: postTypeSchema,
      }),
    ]),
  )
  .and(
    z.discriminatedUnion('is_custom_platform', [
      z.object({
        is_custom_platform: z.literal(true),
        platform: z.string(),
      }),
      z.object({
        is_custom_platform: z.literal(false),
        platform: z.nativeEnum(PlatformShortcode),
      }),
    ]),
  )
export type CustomPost = z.infer<typeof customPostSchema>

export const draftScoringSchema = z.object({
  evaluation_content: z.string(),
  evaluation_score: z.number().transform((value) => value / 2),
})
export type DraftScoring = z.infer<typeof draftScoringSchema>

const scoringSchema = draftScoringSchema.extend({
  id: z.number(),
  user: z.string(),
  user_id: z.number(),
  evaluation_time: z.string(),
})
export type Scoring = z.infer<typeof scoringSchema>

export const collaborationRecordSchema = draftRecordSchema.extend({
  id: z.number(),
  is_new_evaluation: z.literal(true),
  manager_id: z.number().nullable(),
  budget: z.string().transform(Number),
  post_engagement_rate: z.number().nullable(),
  post_list: postSchema.array(),
  scoring_list: scoringSchema.array(),
})
export type CollaborationRecord = z.infer<typeof collaborationRecordSchema>

const customPostCollaborationRecordSchema = collaborationRecordSchema.extend({
  post_list: customPostSchema.array(),
})
export type CustomPostCollaborationRecord = z.infer<
  typeof customPostCollaborationRecordSchema
>

const legacyCollaborationRecordSchema = z.object({
  id: z.number(),
  is_new_evaluation: z.literal(false),
  scoring_list: scoringSchema.array(),
})
export type LegacyCollaborationRecord = z.infer<
  typeof legacyCollaborationRecordSchema
>
export const fetchCollaborationRecordsResponseSchema = z.object({
  data: listResourceSchema.extend({
    record_list: z
      .discriminatedUnion('is_new_evaluation', [
        collaborationRecordSchema,
        legacyCollaborationRecordSchema,
      ])
      .array(),
  }),
})
export type FetchCollaborationRecordsResponse = z.infer<
  typeof fetchCollaborationRecordsResponseSchema
>

export const fetchCustomPostCollaborationRecordsResponseSchema = z.object({
  data: listResourceSchema.extend({
    record_list: z
      .discriminatedUnion('is_new_evaluation', [
        customPostCollaborationRecordSchema,
        legacyCollaborationRecordSchema,
      ])
      .array(),
  }),
})
export type FetchCustomPostCollaborationRecordsResponse = z.infer<
  typeof fetchCustomPostCollaborationRecordsResponseSchema
>

export interface AddCustomizedCollaborationRecordRequest {
  kolId: string
  record: DraftRecord
}

export const addCustomizedCollaborationRecordResponseSchema = z.object({
  customized_collaboration_record_id: z.number(),
})
export type AddCustomizedCollaborationRecordResponse = z.infer<
  typeof addCustomizedCollaborationRecordResponseSchema
>

export interface UpdateCustomizedCollaborationRecordRequest
  extends AddCustomizedCollaborationRecordRequest {
  id: number
}

export interface DeleteCustomizedCollaborationRecordRequest {
  kolId: string
  id: number
}

export enum CollaborationRecordScoringType {
  CampaignKol = 'campaign_kol',
  ReportKol = 'report_kol',
  Customized = 'customized',
}

export interface AddCollaborationRecordScoringRequest extends DraftScoring {
  kolId: string
  collaboration_record_id: number
  collaboration_record_scoring_type: CollaborationRecordScoringType
}

export const addCollaborationRecordScoringResponseSchema = z.object({
  collaboration_record_scoring_id: z.number(),
})
export type AddCollaborationRecordScoringResponse = z.infer<
  typeof addCollaborationRecordScoringResponseSchema
>

export interface EditCollaborationRecordScoringRequest
  extends Omit<
    AddCollaborationRecordScoringRequest,
    'collaboration_record_id'
  > {
  collaborationRecordScoringId: number
}

export interface DeleteCollaborationRecordScoringRequest {
  kolId: string
  collaborationRecordScoringId: number
  collaborationRecordScoringType: CollaborationRecordScoringType
}
